import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import {
  BookOpen, Trophy, Flame, Target, Clock, ArrowRight,
  Sparkles, MessageCircle, Award, TrendingUp, Play,
  LogOut, Settings, Bell, Calendar, FileText, Users,
  CheckCircle, AlertCircle, Eye, Star, User, Mail,
  Send, Upload, Image as ImageIcon, Video, X, Maximize2,
  ChevronLeft, ChevronRight, Circle, Pencil, ZoomIn,
  CreditCard, Receipt, GraduationCap, Building, MapPin, Phone,
  Wallet, IndianRupee, History, Download, Lock, Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

// Student Profile Modal Component
const StudentProfileModal = ({ user, dashboardData, onClose }) => {
  const [activeTab, setActiveTab] = useState('personal');
  
  // Get real enrollment data
  const enrollments = dashboardData?.enrollments || [];
  const currentEnrollment = enrollments.find(e => e.payment_status === 'completed') || enrollments[0];
  const courses = dashboardData?.courses || [];
  const currentCourse = courses.find(c => c.course_id === currentEnrollment?.course_id);
  
  // Calculate stats from real data
  const stats = dashboardData?.stats || {};
  const submissions = dashboardData?.submissions || [];
  const totalSubmissions = submissions.length;
  const reviewedSubmissions = submissions.filter(s => s.status === 'reviewed');
  const avgScore = reviewedSubmissions.length > 0 
    ? Math.round(reviewedSubmissions.reduce((acc, s) => acc + (s.score || 0), 0) / reviewedSubmissions.length)
    : 0;
  
  // Calculate fee info from enrollment
  const totalFee = currentEnrollment?.course_price || 0;
  const amountPaid = currentEnrollment?.payment_status === 'completed' ? currentEnrollment?.payment_amount || totalFee : 0;
  const pendingBalance = totalFee - amountPaid;

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Avatar className="w-12 h-12">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-gradient-to-r from-emerald-500 to-cyan-500 text-white text-lg">
                {user?.name?.split(' ').map(n => n[0]).join('') || 'S'}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="text-xl font-bold">{user?.name || 'Student'}</p>
              <p className="text-sm text-muted-foreground font-normal">{user?.email}</p>
            </div>
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="personal" className="flex items-center gap-2">
              <User className="w-4 h-4" /> Personal
            </TabsTrigger>
            <TabsTrigger value="academics" className="flex items-center gap-2">
              <GraduationCap className="w-4 h-4" /> Academics
            </TabsTrigger>
            <TabsTrigger value="fees" className="flex items-center gap-2">
              <Wallet className="w-4 h-4" /> Fees
            </TabsTrigger>
          </TabsList>

          <ScrollArea className="h-[55vh] mt-4">
            {/* Personal Info Tab */}
            <TabsContent value="personal" className="space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <User className="w-5 h-5 text-emerald-500" />
                    Personal Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Full Name</p>
                      <p className="font-medium">{user?.name || 'Student Name'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Email</p>
                      <p className="font-medium flex items-center gap-2">
                        <Mail className="w-4 h-4 text-muted-foreground" />
                        {user?.email}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Student ID</p>
                      <p className="font-medium">{user?.user_id?.slice(-8).toUpperCase() || 'N/A'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">XP Points</p>
                      <p className="font-medium flex items-center gap-2">
                        <Star className="w-4 h-4 text-yellow-500" />
                        {dashboardData?.user?.xp_points || 0} XP
                      </p>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Member Since</p>
                    <p className="font-medium">
                      {currentEnrollment?.enrolled_at 
                        ? new Date(currentEnrollment.enrolled_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
                        : 'N/A'}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Academics Tab */}
            <TabsContent value="academics" className="space-y-4">
              {/* Current Course */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-blue-500" />
                    Current Enrollment
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {currentCourse ? (
                    <div className="p-4 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-xl border border-blue-500/20">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="font-bold text-lg">{currentCourse.title}</h3>
                          <p className="text-sm text-muted-foreground">Course ID: {currentCourse.course_id}</p>
                        </div>
                        <Badge variant="outline" className={`${
                          currentEnrollment?.payment_status === 'completed' 
                            ? 'bg-emerald-500/10 text-emerald-600' 
                            : 'bg-orange-500/10 text-orange-600'
                        }`}>
                          {currentEnrollment?.payment_status === 'completed' ? 'Active' : 'Payment Pending'}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <div className="text-center p-3 bg-background/50 rounded-lg">
                          <p className="text-2xl font-bold text-emerald-500">{currentEnrollment?.progress || 0}%</p>
                          <p className="text-xs text-muted-foreground">Progress</p>
                        </div>
                        <div className="text-center p-3 bg-background/50 rounded-lg">
                          <p className="text-2xl font-bold text-blue-500">{avgScore || 0}</p>
                          <p className="text-xs text-muted-foreground">Avg. Score</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="p-6 text-center text-muted-foreground">
                      <BookOpen className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p>No active enrollment</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Submission Stats */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="w-5 h-5 text-purple-500" />
                    Assignment Performance
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-muted/30 rounded-lg">
                      <p className="text-2xl font-bold">{totalSubmissions}</p>
                      <p className="text-xs text-muted-foreground">Total Submissions</p>
                    </div>
                    <div className="text-center p-4 bg-muted/30 rounded-lg">
                      <p className="text-2xl font-bold text-emerald-500">{reviewedSubmissions.length}</p>
                      <p className="text-xs text-muted-foreground">Reviewed</p>
                    </div>
                    <div className="text-center p-4 bg-muted/30 rounded-lg">
                      <p className="text-2xl font-bold text-blue-500">{avgScore}%</p>
                      <p className="text-xs text-muted-foreground">Avg Score</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* All Enrollments */}
              {enrollments.length > 1 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">All Enrollments</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {enrollments.map((enrollment, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                        <div>
                          <p className="font-medium">{enrollment.course_title || enrollment.course_id}</p>
                          <p className="text-xs text-muted-foreground">
                            Enrolled: {new Date(enrollment.enrolled_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Badge variant={enrollment.payment_status === 'completed' ? 'default' : 'secondary'}>
                          {enrollment.payment_status === 'completed' ? 'Active' : 'Pending'}
                        </Badge>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Fees Tab */}
            <TabsContent value="fees" className="space-y-4">
              {/* Fee Summary */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <IndianRupee className="w-5 h-5 text-emerald-500" />
                    Fee Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="text-center p-4 bg-muted/30 rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">Total Fee</p>
                      <p className="text-xl font-bold flex items-center justify-center">
                        <IndianRupee className="w-4 h-4" />{totalFee.toLocaleString('en-IN')}
                      </p>
                    </div>
                    <div className="text-center p-4 bg-emerald-500/10 rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">Amount Paid</p>
                      <p className="text-xl font-bold text-emerald-600 flex items-center justify-center">
                        <IndianRupee className="w-4 h-4" />{amountPaid.toLocaleString('en-IN')}
                      </p>
                    </div>
                    <div className="text-center p-4 bg-orange-500/10 rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">Balance</p>
                      <p className="text-xl font-bold text-orange-600 flex items-center justify-center">
                        <IndianRupee className="w-4 h-4" />{pendingBalance.toLocaleString('en-IN')}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Payment Receipts with GST */}
              <FeesReceiptsSection />
            </TabsContent>
          </ScrollArea>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

// Annotated Image Viewer Component
const AnnotatedImageViewer = ({ feedback, onClose }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [displayedDimensions, setDisplayedDimensions] = useState({ width: 800, height: 600 });
  const imgRef = useRef(null);
  const containerRef = useRef(null);

  // Get the canvas size that mentor used for drawing annotations
  // This is critical for accurate scaling - annotations were drawn at this size
  const canvasSize = feedback?.canvas_size || { width: 800, height: 600 };

  // Update dimensions when image loads
  const handleImageLoad = (e) => {
    const img = e.target;
    setDisplayedDimensions({
      width: img.offsetWidth,
      height: img.offsetHeight
    });
    setImageLoaded(true);
  };

  // Recalculate dimensions on window resize
  useEffect(() => {
    const updateDisplayedSize = () => {
      if (imgRef.current) {
        setDisplayedDimensions({
          width: imgRef.current.offsetWidth,
          height: imgRef.current.offsetHeight
        });
      }
    };
    
    window.addEventListener('resize', updateDisplayedSize);
    return () => window.removeEventListener('resize', updateDisplayedSize);
  }, []);

  if (!feedback) return null;

  // Calculate scale factor using mentor's canvas size (not natural image dimensions)
  // Annotations were drawn at canvasSize dimensions, so we scale from that
  const scaleX = displayedDimensions.width / canvasSize.width;
  const scaleY = displayedDimensions.height / canvasSize.height;

  // Render different annotation types with scaling
  const renderAnnotation = (annotation, index) => {
    const color = annotation.color || '#ff0000';
    const lineWidth = (annotation.lineWidth || annotation.strokeWidth || 3) * Math.min(scaleX, scaleY);
    
    // Handle pen/path type annotations with points array
    if ((annotation.type === 'path' || annotation.type === 'pen') && annotation.points && annotation.points.length > 0) {
      const pathData = annotation.points.map((p, i) => 
        `${i === 0 ? 'M' : 'L'} ${p.x * scaleX} ${p.y * scaleY}`
      ).join(' ');
      return (
        <path
          key={index}
          d={pathData}
          stroke={color}
          strokeWidth={lineWidth}
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      );
    }
    
    // Handle circle annotations
    if (annotation.type === 'circle') {
      const cx = (annotation.x || annotation.centerX || 0) * scaleX;
      const cy = (annotation.y || annotation.centerY || 0) * scaleY;
      const r = (annotation.radius || 30) * Math.min(scaleX, scaleY);
      return (
        <circle
          key={index}
          cx={cx}
          cy={cy}
          r={r}
          stroke={color}
          strokeWidth={lineWidth}
          fill="none"
        />
      );
    }
    
    // Handle arrow annotations
    if (annotation.type === 'arrow') {
      const startX = (annotation.startX || annotation.x || 0) * scaleX;
      const startY = (annotation.startY || annotation.y || 0) * scaleY;
      const endX = (annotation.endX || startX + 50) * scaleX;
      const endY = (annotation.endY || startY + 50) * scaleY;
      
      // Calculate arrowhead
      const angle = Math.atan2(endY - startY, endX - startX);
      const arrowLength = 15 * Math.min(scaleX, scaleY);
      const arrowAngle = Math.PI / 6;
      
      const arrow1X = endX - arrowLength * Math.cos(angle - arrowAngle);
      const arrow1Y = endY - arrowLength * Math.sin(angle - arrowAngle);
      const arrow2X = endX - arrowLength * Math.cos(angle + arrowAngle);
      const arrow2Y = endY - arrowLength * Math.sin(angle + arrowAngle);
      
      return (
        <g key={index}>
          <line
            x1={startX}
            y1={startY}
            x2={endX}
            y2={endY}
            stroke={color}
            strokeWidth={lineWidth}
            strokeLinecap="round"
          />
          <polygon
            points={`${endX},${endY} ${arrow1X},${arrow1Y} ${arrow2X},${arrow2Y}`}
            fill={color}
          />
        </g>
      );
    }
    
    // Handle rectangle annotations
    if (annotation.type === 'rectangle' || annotation.type === 'rect') {
      return (
        <rect
          key={index}
          x={(annotation.x || 0) * scaleX}
          y={(annotation.y || 0) * scaleY}
          width={(annotation.width || 50) * scaleX}
          height={(annotation.height || 50) * scaleY}
          stroke={color}
          strokeWidth={lineWidth}
          fill="none"
        />
      );
    }
    
    // Handle text annotations
    if (annotation.type === 'text') {
      const fontSize = (annotation.fontSize || 16) * Math.min(scaleX, scaleY);
      return (
        <text
          key={index}
          x={(annotation.x || 0) * scaleX}
          y={(annotation.y || 0) * scaleY}
          fill={color}
          fontSize={fontSize}
          fontFamily="Arial"
          fontWeight="bold"
        >
          {annotation.text || annotation.content || ''}
        </text>
      );
    }
    
    // Handle highlight/highlighter annotations
    if (annotation.type === 'highlight' || annotation.type === 'highlighter') {
      if (annotation.points && annotation.points.length > 0) {
        const pathData = annotation.points.map((p, i) => 
          `${i === 0 ? 'M' : 'L'} ${p.x * scaleX} ${p.y * scaleY}`
        ).join(' ');
        return (
          <path
            key={index}
            d={pathData}
            stroke={color}
            strokeWidth={lineWidth * 3}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity={0.4}
          />
        );
      }
    }
    
    return null;
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] p-0 overflow-hidden">
        <DialogHeader className="p-4 border-b">
          <DialogTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5" />
            Annotated Submission - {feedback.assignment_title}
          </DialogTitle>
          <DialogDescription>
            Score: {feedback.score}/100 • {feedback.annotations?.length || 0} annotations by {feedback.mentor_name || 'Mentor'}
          </DialogDescription>
        </DialogHeader>
        <div ref={containerRef} className="relative w-full h-[70vh] bg-black flex items-center justify-center overflow-auto">
          <div className="relative inline-block">
            <img
              ref={imgRef}
              src={feedback.file_url}
              alt="Submission"
              className="max-w-full max-h-[70vh] object-contain"
              onLoad={handleImageLoad}
              onError={() => setImageLoaded(true)}
            />
            {/* SVG Overlay for annotations - positioned absolutely over the image */}
            {imageLoaded && feedback.annotations && feedback.annotations.length > 0 && (
              <svg
                className="absolute top-0 left-0 pointer-events-none"
                width={displayedDimensions.width}
                height={displayedDimensions.height}
                style={{ width: displayedDimensions.width, height: displayedDimensions.height }}
              >
                {feedback.annotations.map((annotation, index) => renderAnnotation(annotation, index))}
              </svg>
            )}
            {!imageLoaded && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                <div className="text-center text-white">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                  <p>Loading annotated submission...</p>
                </div>
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="p-4 border-t">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mr-auto">
            <Pencil className="w-4 h-4" />
            {feedback.annotations?.length || 0} annotations from mentor
          </div>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Generate PDF Receipt Function
const generateReceiptPDF = (receipt) => {
  const VIA_LOGO_URL = "https://customer-assets.emergentagent.com/job_via-platform/artifacts/epc7ik46_VIA_LOGO_Tranparent.png";
  
  const formatAmount = (amount) => {
    if (!amount) return '₹0';
    return `₹${(amount / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Create printable HTML content
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Receipt - ${receipt.invoice_number}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
        .header { text-align: center; border-bottom: 3px solid #10b981; padding-bottom: 25px; margin-bottom: 30px; }
        .logo { height: 60px; margin-bottom: 10px; }
        .company-name { font-size: 20px; font-weight: bold; color: #1f2937; margin: 10px 0 5px 0; }
        .invoice-title { font-size: 24px; color: #10b981; margin-top: 15px; font-weight: bold; }
        .info-row { display: flex; justify-content: space-between; margin-bottom: 10px; padding: 8px 0; border-bottom: 1px solid #f3f4f6; }
        .section { margin-bottom: 30px; }
        .section-title { font-size: 14px; font-weight: bold; color: #10b981; margin-bottom: 15px; text-transform: uppercase; letter-spacing: 1px; }
        .amount-table { width: 100%; border-collapse: collapse; }
        .amount-table td { padding: 12px 0; }
        .amount-table .label { color: #6b7280; }
        .amount-table .value { text-align: right; color: #1f2937; }
        .amount-table .total { font-weight: bold; font-size: 18px; border-top: 2px solid #10b981; }
        .total .value { color: #10b981; }
        .gst-section { background: #f0fdf4; padding: 15px; border-radius: 8px; margin-top: 20px; border: 1px solid #d1fae5; }
        .footer { text-align: center; margin-top: 50px; padding-top: 25px; border-top: 2px solid #e5e7eb; color: #6b7280; font-size: 12px; }
        .footer-logo { height: 40px; opacity: 0.7; margin-bottom: 10px; }
        @media print { 
          body { padding: 20px; }
          .header { border-bottom-color: #10b981 !important; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <img src="${VIA_LOGO_URL}" alt="VIA Logo" class="logo" />
        <div class="company-name">Visual Institute of Arts</div>
        <div class="invoice-title">PAYMENT RECEIPT</div>
      </div>
      
      <div class="section">
        <div class="section-title">Invoice Details</div>
        <div class="info-row"><span>Invoice Number:</span><strong>${receipt.invoice_number}</strong></div>
        <div class="info-row"><span>Date:</span><span>${formatDate(receipt.payment_date)}</span></div>
        <div class="info-row"><span>Payment ID:</span><span>${receipt.payment_id || 'N/A'}</span></div>
      </div>
      
      <div class="section">
        <div class="section-title">Student Details</div>
        <div class="info-row"><span>Name:</span><span>${receipt.user_name || 'N/A'}</span></div>
        <div class="info-row"><span>Email:</span><span>${receipt.user_email || 'N/A'}</span></div>
      </div>
      
      <div class="section">
        <div class="section-title">Course Details</div>
        <div class="info-row"><span>Course:</span><span>${receipt.course_title}</span></div>
        <div class="info-row"><span>Plan:</span><span>${receipt.tier_name || receipt.tier}</span></div>
      </div>
      
      <div class="section">
        <div class="section-title">Payment Summary</div>
        <table class="amount-table">
          <tr><td class="label">Base Amount</td><td class="value">${formatAmount(receipt.gst_breakdown?.base_amount)}</td></tr>
          <tr><td class="label">CGST (9%)</td><td class="value">${formatAmount(receipt.gst_breakdown?.cgst)}</td></tr>
          <tr><td class="label">SGST (9%)</td><td class="value">${formatAmount(receipt.gst_breakdown?.sgst)}</td></tr>
          ${receipt.discount_applied > 0 ? `<tr><td class="label">Discount</td><td class="value">-${formatAmount(receipt.discount_applied)}</td></tr>` : ''}
          <tr class="total"><td class="label">Total Paid</td><td class="value">${formatAmount(receipt.final_amount)}</td></tr>
        </table>
        <div class="gst-section">
          <small>GST @ 18% (CGST 9% + SGST 9%) is included in the above amount as per GST Act.</small>
        </div>
      </div>
      
      <div class="footer">
        <img src="${VIA_LOGO_URL}" alt="VIA" class="footer-logo" />
        <p>Thank you for choosing Visual Institute of Arts!</p>
        <p>For any queries, contact: admissions@visual-institute-of-arts.com</p>
      </div>
    </body>
    </html>
  `;

  // Open in new window for printing/saving
  const printWindow = window.open('', '_blank');
  printWindow.document.write(htmlContent);
  printWindow.document.close();
  printWindow.print();
};

// Fees & Receipts Section for Profile Modal
const FeesReceiptsSection = () => {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadReceipts();
  }, []);

  const loadReceipts = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API}/payments/receipts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReceipts(response.data || []);
    } catch (error) {
      console.error('Error loading receipts:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatAmount = (amount) => {
    if (!amount) return '₹0';
    return `₹${(amount / 100).toLocaleString('en-IN')}`;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Receipt className="w-5 h-5 text-purple-500" />
          Payment Receipts
        </CardTitle>
      </CardHeader>
      <CardContent>
        {receipts.length > 0 ? (
          <div className="space-y-3">
            {receipts.map((receipt) => (
              <div key={receipt.receipt_id} className="p-3 bg-muted/30 rounded-lg border">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="font-medium text-sm">{receipt.course_title}</p>
                    <p className="text-xs text-muted-foreground">{receipt.invoice_number}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-emerald-600">{formatAmount(receipt.final_amount)}</p>
                    <p className="text-xs text-muted-foreground">{formatDate(receipt.payment_date)}</p>
                  </div>
                </div>
                
                {/* GST Breakdown */}
                <details className="text-xs mt-2 pt-2 border-t">
                  <summary className="text-muted-foreground cursor-pointer hover:text-foreground">
                    View GST Breakdown
                  </summary>
                  <div className="mt-2 grid grid-cols-2 gap-1 bg-background/50 p-2 rounded">
                    <span className="text-muted-foreground">Base:</span>
                    <span className="text-right">{formatAmount(receipt.gst_breakdown?.base_amount)}</span>
                    <span className="text-muted-foreground">CGST (9%):</span>
                    <span className="text-right">{formatAmount(receipt.gst_breakdown?.cgst)}</span>
                    <span className="text-muted-foreground">SGST (9%):</span>
                    <span className="text-right">{formatAmount(receipt.gst_breakdown?.sgst)}</span>
                  </div>
                </details>

                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full mt-2"
                  onClick={() => generateReceiptPDF(receipt)}
                >
                  <Download className="w-3 h-3 mr-1" />
                  Download Receipt
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-4 text-center text-muted-foreground">
            <Receipt className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No payment receipts yet</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Receipts Tab Component (for sidebar - now removed, kept for reference)
const ReceiptsTab = ({ cardClass }) => {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReceipt, setSelectedReceipt] = useState(null);

  useEffect(() => {
    loadReceipts();
  }, []);

  const loadReceipts = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API}/payments/receipts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReceipts(response.data || []);
    } catch (error) {
      console.error('Error loading receipts:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatAmount = (amount) => {
    if (!amount) return '₹0';
    return `₹${(amount / 100).toLocaleString('en-IN')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold flex items-center gap-2">
        <Receipt className="w-6 h-6" />
        Payment Receipts
      </h2>

      {receipts.length > 0 ? (
        <div className="space-y-4">
          {receipts.map((receipt) => (
            <Card key={receipt.receipt_id} className={cardClass}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-emerald-500" />
                    </div>
                    <div>
                      <p className="font-semibold">{receipt.course_title}</p>
                      <p className="text-sm text-muted-foreground">
                        Invoice: {receipt.invoice_number}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(receipt.payment_date)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-lg text-emerald-600">
                      {formatAmount(receipt.final_amount)}
                    </p>
                    <Badge variant="outline" className="text-xs">
                      {receipt.tier_name || receipt.tier}
                    </Badge>
                  </div>
                </div>

                {/* GST Breakdown - Expandable */}
                <details className="mt-4 pt-4 border-t">
                  <summary className="text-sm text-muted-foreground cursor-pointer hover:text-foreground">
                    View GST Breakdown
                  </summary>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-sm bg-muted/50 p-3 rounded-lg">
                    <span className="text-muted-foreground">Base Amount:</span>
                    <span className="text-right">{formatAmount(receipt.gst_breakdown?.base_amount)}</span>
                    <span className="text-muted-foreground">CGST (9%):</span>
                    <span className="text-right">{formatAmount(receipt.gst_breakdown?.cgst)}</span>
                    <span className="text-muted-foreground">SGST (9%):</span>
                    <span className="text-right">{formatAmount(receipt.gst_breakdown?.sgst)}</span>
                    <span className="font-semibold border-t pt-2">Total:</span>
                    <span className="text-right font-semibold border-t pt-2">{formatAmount(receipt.final_amount)}</span>
                  </div>
                </details>

                {/* Actions */}
                <div className="flex gap-2 mt-4">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => generateReceiptPDF(receipt)}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Invoice
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className={cardClass}>
          <CardContent className="p-8 text-center">
            <Receipt className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="font-bold text-lg mb-2">No Receipts Yet</h3>
            <p className="text-muted-foreground">
              Your payment receipts will appear here after you enroll in a course
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default function StudentDashboard() {
  const { user, logout } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('courses');
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [showAnnotatedImage, setShowAnnotatedImage] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [submittingAssignment, setSubmittingAssignment] = useState(null);
  const [submissionFile, setSubmissionFile] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const fileInputRef = useRef(null);
  const chatEndRef = useRef(null);
  const wsRef = useRef(null);
  const pollingRef = useRef(null);
  const dashboardPollingRef = useRef(null);

  // Check if dashboard should be locked (no paid enrollments)
  const dashboardLocked = dashboardData && !dashboardData.dashboard_unlocked && dashboardData.pending_payments?.length > 0;
  const pendingPayments = dashboardData?.pending_payments || [];

  // Handle payment confirmation (mock - in production, integrate with Razorpay)
  const handlePayment = async (enrollment) => {
    setIsProcessingPayment(true);
    try {
      const token = localStorage.getItem('auth_token');
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      
      // In production, this would be called after Razorpay payment success
      // For now, we'll simulate a successful payment
      const response = await axios.post(
        `${API}/enrollments/${enrollment.enrollment_id}/confirm-payment`,
        { 
          payment_id: `pay_${Date.now()}`,
          amount: enrollment.amount_due
        },
        { headers, withCredentials: true }
      );
      
      toast.success(`Payment confirmed! Welcome to ${response.data.course_title}`);
      
      // Reload dashboard to unlock
      await loadDashboardData();
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Payment processing failed. Please try again.');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // Load dashboard data for polling (submissions, feedback updates)
  const loadDashboardDataForPolling = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;
      
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/student/dashboard`, { 
        headers, 
        withCredentials: true 
      });
      
      if (response.data) {
        setDashboardData(response.data);
        setNotifications(response.data.notifications || []);
      }
    } catch (err) {
      console.log('Error polling dashboard:', err);
    }
  }, []);

  // Polling for real-time submission updates when on feedback or assignments tab
  useEffect(() => {
    if (activeTab === 'feedback' || activeTab === 'assignments' || activeTab === 'overview') {
      // Immediate load when tab changes
      loadDashboardDataForPolling();
      // Start polling every 5 seconds for real-time feel
      dashboardPollingRef.current = setInterval(loadDashboardDataForPolling, 5000);
    }
    
    return () => {
      if (dashboardPollingRef.current) {
        clearInterval(dashboardPollingRef.current);
      }
    };
  }, [activeTab, loadDashboardDataForPolling]);

  // Load messages function
  const loadMessages = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;
      
      const headers = { Authorization: `Bearer ${token}` };
      const messagesRes = await axios.get(`${API}/messages`, { headers, withCredentials: true });
      
      if (messagesRes.data) {
        setMessages(messagesRes.data);
      }
    } catch (err) {
      console.log('Error loading messages:', err);
    }
  }, []);

  // Polling for real-time messages when chat is active
  useEffect(() => {
    if (activeTab === 'messages') {
      // Start polling every 2 seconds for real-time feel
      pollingRef.current = setInterval(loadMessages, 2000);
      loadMessages(); // Initial load
    }
    
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [activeTab, loadMessages]);

  // WebSocket connection for real-time messaging (backup/enhancement)
  useEffect(() => {
    if (!user?.user_id) return;

    const connectWebSocket = () => {
      try {
        const wsUrl = `${WS_URL}/ws/user/${user.user_id}`;
        wsRef.current = new WebSocket(wsUrl);

        wsRef.current.onopen = () => {
          console.log('WebSocket connected');
          const pingInterval = setInterval(() => {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
              wsRef.current.send(JSON.stringify({ type: 'ping' }));
            }
          }, 30000);
          wsRef.current.pingInterval = pingInterval;
        };

        wsRef.current.onmessage = (event) => {
          const data = JSON.parse(event.data);
          if (data.type === 'new_message') {
            // Refresh messages when new one arrives
            loadMessages();
            toast.success(`New message from ${data.message.sender_name}`);
          }
        };

        wsRef.current.onclose = () => {
          console.log('WebSocket disconnected, using polling fallback');
        };

        wsRef.current.onerror = (error) => {
          console.log('WebSocket unavailable, using polling fallback');
        };
      } catch (error) {
        console.log('WebSocket connection failed, using polling fallback');
      }
    };

    connectWebSocket();

    return () => {
      if (wsRef.current?.pingInterval) {
        clearInterval(wsRef.current.pingInterval);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [user?.user_id, loadMessages]);

  // Auto-scroll to bottom of chat
  useEffect(() => {
    if (activeTab === 'messages' && chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, activeTab]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(`${API}/student/dashboard`, { 
        headers, 
        withCredentials: true 
      });
      
      setDashboardData(response.data);
      setNotifications(response.data.notifications || []);
      
      // Load messages
      try {
        const messagesRes = await axios.get(`${API}/messages`, { headers, withCredentials: true });
        setMessages(messagesRes.data || []);
      } catch (err) {
        console.log('No messages');
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
      setDashboardData({
        user: { name: user?.name, xp_points: 0, badges: [], streak_days: 0, level: 1 },
        mentor: null,
        courses: [],
        submissions: [],
        assignments: [],
        notifications: [],
        review_feedback: [],
        stats: { total_submissions: 0, pending_reviews: 0, reviewed: 0, avg_score: 0 }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    if (wsRef.current) wsRef.current.close();
    await logout();
    navigate('/');
  };

  const markNotificationRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setNotifications(prev => prev.map(n => 
        n.notification_id === notificationId ? { ...n, read: true } : n
      ));
    } catch (error) {
      console.error('Error marking notification read:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() || !dashboardData?.mentor?.user_id) {
      toast.error('Please enter a message');
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(`${API}/messages`, {
        recipient_id: dashboardData.mentor.user_id,
        content: messageText
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      
      // Add message to local state immediately
      if (response.data.data) {
        setMessages(prev => [response.data.data, ...prev]);
      }
      
      toast.success(`Message sent!`);
      setMessageText('');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    }
  };

  const handleSubmitAssignment = async () => {
    if (!submittingAssignment) return;
    
    if (!submissionFile) {
      toast.error('Please upload a file or provide an image URL');
      return;
    }

    // Prevent double submission
    if (isSubmitting) {
      toast.warning('Submission in progress, please wait...');
      return;
    }

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('auth_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const submissionData = {
        assignment_id: submittingAssignment.assignment_id,
        assignment_title: submittingAssignment.title,
        course_id: submittingAssignment.course_id,
        course_name: submittingAssignment.course_name,
        file_url: submissionFile,
        file_type: 'image'
      };
      
      await axios.post(`${API}/submissions`, submissionData, { headers, withCredentials: true });
      
      toast.success(`Assignment "${submittingAssignment.title}" submitted! Waiting for mentor review.`);
      setSubmittingAssignment(null);
      setSubmissionFile(null);
      setUploadedFileName('');
      
      loadDashboardData();
    } catch (error) {
      console.error('Error submitting assignment:', error);
      toast.error('Failed to submit assignment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPG, PNG, WebP, or GIF images.');
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }

    setIsUploading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true
      });

      setSubmissionFile(response.data.file_url);
      setUploadedFileName(file.name);
      toast.success(`File "${file.name}" uploaded successfully!`);
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file. Please try again or use a URL.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      const fakeEvent = { target: { files: [file] } };
      await handleFileUpload(fakeEvent);
    }
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Group messages by date for chat thread view
  const groupMessagesByDate = (messages) => {
    const groups = {};
    messages.forEach(msg => {
      const date = new Date(msg.created_at).toLocaleDateString();
      if (!groups[date]) groups[date] = [];
      groups[date].push(msg);
    });
    return groups;
  };

  const cardClass = theme === 'dark' ? '' : 'shadow-lg';
  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const { mentor, courses, submissions, assignments, review_feedback, stats } = dashboardData || {};

  // Payment Required Overlay - Freezes dashboard until payment is complete
  if (dashboardLocked) {
    return (
      <div className="min-h-screen bg-background" data-testid="student-dashboard">
        {/* Top Header Bar - Limited */}
        <header className="sticky top-0 z-50 bg-card border-b border-border px-6 py-4">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <div className="flex items-center gap-4">
              <img
                src="https://customer-assets.emergentagent.com/job_via-platform/artifacts/epc7ik46_VIA_LOGO_Tranparent.png"
                alt="VIA Logo"
                className="w-14 h-14 object-contain"
              />
              <div>
                <h1 className="text-xl font-bold">Student Dashboard</h1>
                <p className="text-sm text-muted-foreground">Welcome, {dashboardData?.user?.name?.split(' ')[0] || user?.name?.split(' ')[0]}</p>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={handleLogout}>
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </header>

        {/* Payment Required Screen */}
        <div className="flex items-center justify-center min-h-[80vh] p-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-lg w-full"
          >
            <Card className={`${cardClass} overflow-hidden`}>
              {/* Header */}
              <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-6 text-white">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                    <Lock className="w-8 h-8" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Payment Required</h2>
                    <p className="text-white/80">Complete payment to unlock your dashboard</p>
                  </div>
                </div>
              </div>

              <CardContent className="p-6 space-y-6">
                <div className="text-center py-4">
                  <p className="text-muted-foreground mb-4">
                    Your enrollment is confirmed! Please complete the payment to access your course content, assignments, and connect with your mentor.
                  </p>
                </div>

                {/* Pending Payments List */}
                <div className="space-y-4">
                  {pendingPayments.map((payment) => (
                    <div
                      key={payment.enrollment_id}
                      className="p-4 rounded-lg border bg-muted/30 space-y-3"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                            <BookOpen className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <h3 className="font-semibold">{payment.course_title || payment.course_id}</h3>
                            <p className="text-sm text-muted-foreground">Course Enrollment</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-emerald-600">
                            ₹{payment.amount_due?.toLocaleString('en-IN') || '0'}
                          </p>
                          <p className="text-xs text-muted-foreground">Amount Due</p>
                        </div>
                      </div>

                      <Button
                        className="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600"
                        onClick={() => handlePayment(payment)}
                        disabled={isProcessingPayment}
                      >
                        {isProcessingPayment ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Processing Payment...
                          </>
                        ) : (
                          <>
                            <CreditCard className="w-4 h-4 mr-2" />
                            Pay Now
                          </>
                        )}
                      </Button>
                    </div>
                  ))}
                </div>

                {/* What you get after payment */}
                <div className="pt-4 border-t">
                  <h4 className="font-medium mb-3">What you&apos;ll get access to:</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <CheckCircle className="w-4 h-4 text-emerald-500" />
                      <span>Course Videos</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <CheckCircle className="w-4 h-4 text-emerald-500" />
                      <span>Mentor Support</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <CheckCircle className="w-4 h-4 text-emerald-500" />
                      <span>Assignments</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <CheckCircle className="w-4 h-4 text-emerald-500" />
                      <span>Certificates</span>
                    </div>
                  </div>
                </div>

                <p className="text-xs text-center text-muted-foreground pt-2">
                  Secure payment powered by Razorpay. Your payment information is encrypted.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="student-dashboard">
      {/* Top Header Bar */}
      <header className="sticky top-0 z-50 bg-card border-b border-border px-6 py-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            <img
              src="https://customer-assets.emergentagent.com/job_via-platform/artifacts/epc7ik46_VIA_LOGO_Tranparent.png"
              alt="VIA Logo"
              className="w-14 h-14 object-contain"
            />
            <div>
              <h1 className="text-xl font-bold">Student Dashboard</h1>
              <p className="text-sm text-muted-foreground">Welcome back, {dashboardData?.user?.name?.split(' ')[0] || user?.name?.split(' ')[0]}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/20">
              <Trophy className="w-4 h-4 text-emerald-500" />
              <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">{dashboardData?.user?.xp_points || 0} XP</span>
            </div>
            <Button variant="ghost" size="icon" className="relative" onClick={() => setShowNotifications(true)}>
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-xs text-white flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </Button>
            
            {/* Profile Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                  <Avatar className="h-10 w-10 cursor-pointer ring-2 ring-emerald-500/30 hover:ring-emerald-500/60 transition-all">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-gradient-to-r from-emerald-500 to-cyan-500 text-white">
                      {user?.name?.split(' ').map(n => n[0]).join('') || 'S'}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" forceMount>
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">{user?.name || 'Student'}</p>
                    <p className="text-xs leading-none text-muted-foreground">{user?.email}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setShowProfileModal(true)}>
                  <User className="mr-2 h-4 w-4" />
                  <span>Profile</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowProfileModal(true)}>
                  <GraduationCap className="mr-2 h-4 w-4" />
                  <span>My Academics</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowProfileModal(true)}>
                  <Wallet className="mr-2 h-4 w-4" />
                  <span>Fees & Payments</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem>
                  <Settings className="mr-2 h-4 w-4" />
                  <span>Settings</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Log out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 min-h-[calc(100vh-73px)] bg-card border-r border-border p-4 hidden lg:block">
          <nav className="space-y-2">
            <Button variant={activeTab === 'courses' ? 'secondary' : 'ghost'} className="w-full justify-start" onClick={() => setActiveTab('courses')}>
              <BookOpen className="w-4 h-4 mr-2" /> My Courses
            </Button>
            <Button variant={activeTab === 'assignments' ? 'secondary' : 'ghost'} className="w-full justify-start" onClick={() => setActiveTab('assignments')}>
              <FileText className="w-4 h-4 mr-2" /> Assignments
              {assignments?.length > 0 && (
                <Badge variant="outline" className="ml-auto">{assignments.length}</Badge>
              )}
            </Button>
            <Button variant={activeTab === 'feedback' ? 'secondary' : 'ghost'} className="w-full justify-start" onClick={() => setActiveTab('feedback')}>
              <CheckCircle className="w-4 h-4 mr-2" /> Review Feedback
              {review_feedback?.length > 0 && (
                <Badge variant="outline" className="ml-auto">{review_feedback.length}</Badge>
              )}
            </Button>
            <Button variant={activeTab === 'messages' ? 'secondary' : 'ghost'} className="w-full justify-start" onClick={() => setActiveTab('messages')}>
              <MessageCircle className="w-4 h-4 mr-2" /> Messages
              {messages.filter(m => !m.read && m.recipient_id === user?.user_id).length > 0 && (
                <Badge variant="destructive" className="ml-auto">{messages.filter(m => !m.read && m.recipient_id === user?.user_id).length}</Badge>
              )}
            </Button>
            <Button variant={activeTab === 'mentor' ? 'secondary' : 'ghost'} className="w-full justify-start" onClick={() => setActiveTab('mentor')}>
              <User className="w-4 h-4 mr-2" /> My Mentor
            </Button>
            <Button variant="ghost" className="w-full justify-start" onClick={() => navigate('/dashboard/ai-tutor')}>
              <Sparkles className="w-4 h-4 mr-2" /> AI Tutor
            </Button>
            <Button variant="ghost" className="w-full justify-start" onClick={() => navigate('/dashboard/mock-tests')} data-testid="mock-tests-nav">
              <BookOpen className="w-4 h-4 mr-2" /> Mock Tests
            </Button>
            <Button variant="ghost" className="w-full justify-start">
              <Trophy className="w-4 h-4 mr-2" /> Leaderboard
            </Button>
            
            {/* Explore More Courses - Prominent CTA */}
            <div className="mt-4 pt-4 border-t border-border">
              <Button 
                className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white"
                onClick={() => navigate('/courses')}
              >
                <BookOpen className="w-4 h-4 mr-2" />
                Buy More Courses
              </Button>
            </div>
          </nav>

          {/* Level Progress */}
          <div className="mt-8 p-4 rounded-xl bg-muted/50">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-primary" />
              Level Progress
            </h3>
            <div className="mb-2">
              <div className="flex justify-between text-sm mb-1">
                <span>Level {dashboardData?.user?.level || 1}</span>
                <span>{(dashboardData?.user?.xp_points || 0) % 500}/500 XP</span>
              </div>
              <Progress value={((dashboardData?.user?.xp_points || 0) % 500) / 5} className="h-2" />
            </div>
            <p className="text-xs text-muted-foreground">
              {500 - ((dashboardData?.user?.xp_points || 0) % 500)} XP to next level
            </p>
          </div>

          {/* Streak */}
          <div className="mt-4 p-4 rounded-xl bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/20">
            <div className="flex items-center gap-2">
              <Flame className="w-5 h-5 text-orange-500" />
              <span className="font-bold text-lg">{dashboardData?.user?.streak_days || 0} Day Streak</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Keep learning daily!</p>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 max-w-6xl">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[
              { icon: Trophy, label: 'XP Points', value: dashboardData?.user?.xp_points || 0, color: 'text-yellow-500' },
              { icon: FileText, label: 'Submissions', value: stats?.total_submissions || 0, color: 'text-blue-500' },
              { icon: CheckCircle, label: 'Reviewed', value: stats?.reviewed || 0, color: 'text-emerald-500' },
              { icon: Star, label: 'Avg Score', value: `${stats?.avg_score || 0}%`, color: 'text-purple-500' }
            ].map((stat, i) => (
              <Card key={i} className={cardClass}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl bg-muted flex items-center justify-center ${stat.color}`}>
                    <stat.icon className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stat.value}</p>
                    <p className="text-sm text-muted-foreground">{stat.label}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Courses Tab */}
          {activeTab === 'courses' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold">My Courses</h2>
              {courses && courses.length > 0 ? (
                <div className="space-y-6">
                  {courses.map((course) => (
                    <Card key={course.course_id} className={cardClass}>
                      {/* Course Header */}
                      <div className="p-4 border-b bg-muted/30">
                        <div className="flex items-start gap-4">
                          <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0">
                            <img
                              src={course.thumbnail || 'https://images.unsplash.com/photo-1561070791-2526d30994b5'}
                              alt={course.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline">{course.course_id}</Badge>
                              <Badge className="bg-emerald-500/10 text-emerald-600">
                                {course.completed_module_count || 0}/{course.total_modules || 0} Modules
                              </Badge>
                            </div>
                            <h3 className="font-bold text-xl mb-1">{course.title}</h3>
                            <p className="text-sm text-muted-foreground">
                              {course.skills?.join(' → ')}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Modules List */}
                      <CardContent className="p-4">
                        <h4 className="font-semibold mb-4 flex items-center gap-2">
                          <BookOpen className="w-4 h-4" />
                          Course Modules
                          <span className="text-xs text-muted-foreground font-normal ml-2">
                            (Complete each module to unlock the next)
                          </span>
                        </h4>
                        
                        <div className="space-y-3">
                          {(course.modules || []).map((module, idx) => (
                            <div
                              key={module.module_id}
                              className={`p-4 rounded-xl border transition-all ${
                                module.is_completed 
                                  ? 'bg-emerald-500/5 border-emerald-500/30' 
                                  : module.is_unlocked 
                                    ? 'bg-blue-500/5 border-blue-500/30 cursor-pointer hover:bg-blue-500/10' 
                                    : 'bg-muted/30 border-muted opacity-60'
                              }`}
                              onClick={() => {
                                if (module.is_unlocked && !module.is_completed) {
                                  navigate(`/dashboard/course/${course.course_id}/module/${module.skill}`);
                                }
                              }}
                            >
                              <div className="flex items-center gap-4">
                                {/* Module Number */}
                                <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${
                                  module.is_completed 
                                    ? 'bg-emerald-500 text-white' 
                                    : module.is_unlocked 
                                      ? 'bg-blue-500 text-white' 
                                      : 'bg-muted text-muted-foreground'
                                }`}>
                                  {module.is_completed ? (
                                    <CheckCircle className="w-6 h-6" />
                                  ) : module.is_unlocked ? (
                                    `M${module.module_number}`
                                  ) : (
                                    <Lock className="w-5 h-5" />
                                  )}
                                </div>
                                
                                {/* Module Info */}
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <h5 className="font-semibold">{module.title}</h5>
                                    {module.is_completed && (
                                      <Badge className="bg-emerald-500/20 text-emerald-600 text-xs">Completed</Badge>
                                    )}
                                    {!module.is_unlocked && (
                                      <Badge variant="secondary" className="text-xs">
                                        <Lock className="w-3 h-3 mr-1" />
                                        {module.unlock_message}
                                      </Badge>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                    <span className="flex items-center gap-1">
                                      <Play className="w-3 h-3" />
                                      {module.total_videos} videos
                                    </span>
                                    <span className="flex items-center gap-1">
                                      <FileText className="w-3 h-3" />
                                      {module.assignments?.length || 0} assignments
                                    </span>
                                    {module.is_unlocked && !module.is_completed && (
                                      <span className="flex items-center gap-1 text-blue-500">
                                        <TrendingUp className="w-3 h-3" />
                                        {module.progress}% complete
                                      </span>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Action Button */}
                                {module.is_unlocked && !module.is_completed && (
                                  <Button 
                                    size="sm" 
                                    className="bg-gradient-to-r from-blue-500 to-cyan-500"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/dashboard/course/${course.course_id}/module/${module.skill}`);
                                    }}
                                  >
                                    <Play className="w-4 h-4 mr-1" />
                                    Continue
                                  </Button>
                                )}
                                {module.is_completed && (
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/dashboard/course/${course.course_id}/module/${module.skill}`);
                                    }}
                                  >
                                    <Eye className="w-4 h-4 mr-1" />
                                    Review
                                  </Button>
                                )}
                              </div>
                              
                              {/* Progress Bar for unlocked modules */}
                              {module.is_unlocked && !module.is_completed && module.progress > 0 && (
                                <div className="mt-3 ml-16">
                                  <Progress value={module.progress} className="h-1.5" />
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card className={cardClass}>
                  <CardContent className="p-8 text-center">
                    <BookOpen className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                    <h3 className="font-bold text-lg mb-2">No courses yet</h3>
                    <p className="text-muted-foreground mb-4">Start your learning journey by enrolling in a course</p>
                    <Button onClick={() => navigate('/courses')} className="bg-gradient-to-r from-emerald-500 to-cyan-500 text-white">
                      Browse Courses
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Explore More Courses CTA */}
              <Card className={`${cardClass} mt-6 border-dashed border-2 border-emerald-500/30 bg-gradient-to-r from-emerald-500/5 to-teal-500/5`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center">
                        <Sparkles className="w-6 h-6 text-emerald-500" />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg">Expand Your Skills</h3>
                        <p className="text-sm text-muted-foreground">
                          Explore more courses and continue your creative journey
                        </p>
                      </div>
                    </div>
                    <Button 
                      onClick={() => navigate('/courses')} 
                      className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white"
                    >
                      <BookOpen className="w-4 h-4 mr-2" />
                      Explore Courses
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Assignments Tab */}
          {activeTab === 'assignments' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold">My Assignments</h2>
                <p className="text-sm text-muted-foreground">
                  Submissions are due every Saturday. Missed deadlines will lock the assignment.
                </p>
              </div>
              {assignments && assignments.length > 0 ? (
                <div className="space-y-4">
                  {assignments.map((assignment) => {
                    // Check if student has already submitted this assignment
                    const existingSubmission = submissions?.find(
                      sub => sub.assignment_id === assignment.assignment_id
                    );
                    const submissionStatus = existingSubmission?.status;
                    const submissionScore = existingSubmission?.score;
                    const isLocked = assignment.is_locked && !existingSubmission;
                    
                    return (
                      <Card key={assignment.assignment_id} className={`${cardClass} ${isLocked ? 'opacity-60' : ''}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start gap-4">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                              isLocked ? 'bg-gray-500/10' :
                              submissionStatus === 'reviewed' ? 'bg-emerald-500/10' :
                              submissionStatus === 'pending' ? 'bg-orange-500/10' :
                              'bg-blue-500/10'
                            }`}>
                              {isLocked ? (
                                <Lock className="w-6 h-6 text-gray-500" />
                              ) : submissionStatus === 'reviewed' ? (
                                <CheckCircle className="w-6 h-6 text-emerald-500" />
                              ) : submissionStatus === 'pending' ? (
                                <Clock className="w-6 h-6 text-orange-500" />
                              ) : (
                                <FileText className="w-6 h-6 text-blue-500" />
                              )}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                {assignment.week_number && (
                                  <Badge variant="secondary" className="text-xs">Week {assignment.week_number}</Badge>
                                )}
                                <h3 className="font-bold">{assignment.title}</h3>
                                <Badge variant="outline">{assignment.course_name}</Badge>
                                {isLocked && (
                                  <Badge className="bg-gray-500/10 text-gray-600 border-gray-500/20">
                                    <Lock className="w-3 h-3 mr-1" />
                                    Locked
                                  </Badge>
                                )}
                                {submissionStatus === 'reviewed' && submissionScore && (
                                  <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20">
                                    Score: {submissionScore}/100
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground mb-2">{assignment.description}</p>
                              <div className="flex items-center gap-4 text-sm">
                                <span className={`flex items-center gap-1 ${isLocked ? 'text-red-500' : 'text-muted-foreground'}`}>
                                  <Calendar className="w-4 h-4" />
                                  Due: {new Date(assignment.due_date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}
                                  {isLocked && ' (Missed)'}
                                </span>
                                <span className="flex items-center gap-1 text-muted-foreground">
                                  <Star className="w-4 h-4" />
                                  Max Score: {assignment.max_score}
                                </span>
                              </div>
                            </div>
                            {/* Show status badge or submit button based on submission status */}
                            {isLocked ? (
                              <div className="text-center">
                                <Badge variant="outline" className="px-4 py-2 bg-gray-500/10 text-gray-600 border-gray-500/30">
                                  <Lock className="w-4 h-4 mr-2" />
                                  Deadline Passed
                                </Badge>
                                <p className="text-xs text-red-500 mt-1">Impacts Ranking</p>
                              </div>
                            ) : submissionStatus === 'reviewed' ? (
                              <Button 
                                variant="outline"
                                className="border-emerald-500/30 text-emerald-600 hover:bg-emerald-500/10"
                                onClick={() => {
                                  // Find the corresponding feedback and show it
                                  const feedback = review_feedback?.find(f => 
                                    f.submission_id === existingSubmission?.submission_id
                                  );
                                  if (feedback) {
                                    setSelectedFeedback(feedback);
                                    setActiveTab('feedback');
                                  }
                                }}
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                View Feedback
                              </Button>
                            ) : submissionStatus === 'pending' ? (
                              <Badge 
                                variant="outline" 
                                className="px-4 py-2 bg-orange-500/10 text-orange-600 border-orange-500/30"
                              >
                                <Clock className="w-4 h-4 mr-2" />
                                Pending Review
                              </Badge>
                            ) : (
                              <Button 
                                className="bg-gradient-to-r from-emerald-500 to-cyan-500"
                                onClick={() => setSubmittingAssignment(assignment)}
                              >
                                <Upload className="w-4 h-4 mr-2" />
                                Submit
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              ) : (
                <Card className={cardClass}>
                  <CardContent className="p-8 text-center">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                    <h3 className="font-bold text-lg mb-2">No assignments yet</h3>
                    <p className="text-muted-foreground">Your assignments will appear here when your mentor assigns them</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Review Feedback Tab - REDESIGNED SIDE-BY-SIDE */}
          {activeTab === 'feedback' && (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold">Review Feedback</h2>
              {review_feedback && review_feedback.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left: List of Reviews */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Reviewed Submissions</h3>
                    {review_feedback.map((feedback, i) => (
                      <Card 
                        key={i} 
                        className={`cursor-pointer transition-all ${selectedFeedback === feedback ? 'ring-2 ring-primary' : 'hover:border-primary/50'} ${cardClass}`}
                        onClick={() => setSelectedFeedback(feedback)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                              feedback.score >= 80 ? 'bg-emerald-500/10' : feedback.score >= 60 ? 'bg-yellow-500/10' : 'bg-red-500/10'
                            }`}>
                              <span className={`text-lg font-bold ${
                                feedback.score >= 80 ? 'text-emerald-500' : feedback.score >= 60 ? 'text-yellow-500' : 'text-red-500'
                              }`}>
                                {feedback.score}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold truncate">{feedback.assignment_title}</h4>
                              <p className="text-xs text-muted-foreground">
                                {feedback.mentor_name} • {formatTimeAgo(feedback.reviewed_at)}
                              </p>
                            </div>
                            {feedback.annotations?.length > 0 && (
                              <Badge variant="secondary" className="flex-shrink-0">
                                <Pencil className="w-3 h-3 mr-1" />
                                {feedback.annotations.length}
                              </Badge>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {/* Right: Selected Review Details */}
                  <div className="lg:sticky lg:top-24 h-fit">
                    {selectedFeedback ? (
                      <Card className={`${cardClass} overflow-hidden`}>
                        <CardHeader className="pb-4">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-lg">{selectedFeedback.assignment_title}</CardTitle>
                            <Badge variant={selectedFeedback.score >= 80 ? 'default' : selectedFeedback.score >= 60 ? 'secondary' : 'destructive'}>
                              {selectedFeedback.score}/100
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          {/* Score visualization */}
                          <div className="flex items-center gap-4 p-4 rounded-xl bg-muted/50">
                            <div className={`w-20 h-20 rounded-full flex items-center justify-center ${
                              selectedFeedback.score >= 80 ? 'bg-emerald-500/20' : selectedFeedback.score >= 60 ? 'bg-yellow-500/20' : 'bg-red-500/20'
                            }`}>
                              <span className={`text-3xl font-bold ${
                                selectedFeedback.score >= 80 ? 'text-emerald-500' : selectedFeedback.score >= 60 ? 'text-yellow-500' : 'text-red-500'
                              }`}>
                                {selectedFeedback.score}
                              </span>
                            </div>
                            <div>
                              <p className="font-semibold text-lg">
                                {selectedFeedback.score >= 80 ? 'Excellent Work!' : selectedFeedback.score >= 60 ? 'Good Effort!' : 'Needs Improvement'}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                Reviewed by {selectedFeedback.mentor_name}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {selectedFeedback.reviewed_at ? new Date(selectedFeedback.reviewed_at).toLocaleString() : 'Recently'}
                              </p>
                            </div>
                          </div>

                          {/* Written Feedback */}
                          <div>
                            <h4 className="font-medium mb-2 flex items-center gap-2">
                              <MessageCircle className="w-4 h-4" />
                              Mentor&apos;s Feedback
                            </h4>
                            <p className="text-sm text-muted-foreground p-3 rounded-lg bg-muted/50 whitespace-pre-wrap">
                              {selectedFeedback.feedback || 'No written feedback provided'}
                            </p>
                          </div>

                          {/* Visual Annotations */}
                          {selectedFeedback.annotations && selectedFeedback.annotations.length > 0 && (
                            <div>
                              <h4 className="font-medium mb-2 flex items-center gap-2">
                                <Pencil className="w-4 h-4" />
                                Visual Annotations ({selectedFeedback.annotations.length})
                              </h4>
                              <Button 
                                className="w-full bg-gradient-to-r from-purple-500 to-pink-500" 
                                onClick={() => setShowAnnotatedImage(true)}
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                View Annotated Submission
                              </Button>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ) : (
                      <Card className={cardClass}>
                        <CardContent className="p-8 text-center">
                          <Eye className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                          <p className="text-muted-foreground">Select a review to see details</p>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </div>
              ) : (
                <Card className={cardClass}>
                  <CardContent className="p-8 text-center">
                    <CheckCircle className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                    <h3 className="font-bold text-lg mb-2">No feedback yet</h3>
                    <p className="text-muted-foreground">Your mentor&apos;s review feedback will appear here after they review your submissions</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Messages Tab - MODERN CHAT INTERFACE */}
          {activeTab === 'messages' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold">Chat with Mentor</h2>
                {mentor && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
                    {mentor.name}
                  </div>
                )}
              </div>
              
              <Card className={`${cardClass} overflow-hidden`}>
                {/* Chat Messages Area */}
                <ScrollArea className="h-[400px] p-4">
                  <div className="space-y-4">
                    {messages.length > 0 ? (
                      [...messages].reverse().map((msg, index) => {
                        const isMe = msg.sender_id === user?.user_id;
                        return (
                          <motion.div
                            key={msg.message_id || index}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                          >
                            <div className={`flex gap-2 max-w-[80%] ${isMe ? 'flex-row-reverse' : ''}`}>
                              <Avatar className="w-8 h-8 flex-shrink-0">
                                <AvatarFallback className={`text-xs ${
                                  isMe 
                                    ? 'bg-gradient-to-r from-emerald-500 to-cyan-500' 
                                    : 'bg-gradient-to-r from-purple-500 to-pink-500'
                                } text-white`}>
                                  {(isMe ? 'ME' : msg.sender_name?.split(' ').map(n => n[0]).join(''))}
                                </AvatarFallback>
                              </Avatar>
                              <div className={`rounded-2xl px-4 py-2 ${
                                isMe 
                                  ? 'bg-gradient-to-r from-emerald-500 to-cyan-500 text-white' 
                                  : 'bg-muted'
                              }`}>
                                <p className="text-sm">{msg.content}</p>
                                <p className={`text-xs mt-1 ${isMe ? 'text-white/70' : 'text-muted-foreground'}`}>
                                  {formatTimeAgo(msg.created_at)}
                                </p>
                              </div>
                            </div>
                          </motion.div>
                        );
                      })
                    ) : (
                      <div className="text-center py-12 text-muted-foreground">
                        <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-30" />
                        <p className="text-lg font-medium mb-2">Start a conversation</p>
                        <p className="text-sm">Send a message to your mentor to begin</p>
                      </div>
                    )}
                    <div ref={chatEndRef} />
                  </div>
                </ScrollArea>

                {/* Message Input */}
                <div className="border-t p-4">
                  {mentor ? (
                    <div className="flex gap-3">
                      <Textarea
                        value={messageText}
                        onChange={(e) => setMessageText(e.target.value)}
                        placeholder={`Message ${mentor.name}...`}
                        className="min-h-[44px] max-h-[120px] resize-none"
                        rows={1}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage();
                          }
                        }}
                      />
                      <Button 
                        onClick={handleSendMessage}
                        disabled={!messageText.trim()}
                        className="bg-gradient-to-r from-emerald-500 to-cyan-500 px-6"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-2">
                      No mentor assigned yet. Enroll in a course to get a mentor.
                    </p>
                  )}
                </div>
              </Card>
            </div>
          )}

          {/* Mentor Tab */}
          {activeTab === 'mentor' && (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold">My Mentor</h2>
              {mentor ? (
                <Card className={cardClass}>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-6">
                      <Avatar className="w-24 h-24">
                        <AvatarFallback className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-2xl">
                          {mentor.name?.split(' ').map(n => n[0]).join('')}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold mb-1">{mentor.name}</h3>
                        {mentor.vendor_id && (
                          <Badge variant="outline" className="font-mono mb-3">{mentor.vendor_id}</Badge>
                        )}
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Mail className="w-4 h-4" />
                            {mentor.email}
                          </div>
                          {mentor.subjects && mentor.subjects.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-3">
                              {mentor.subjects.map((subject, i) => (
                                <Badge key={i} variant="secondary">{subject}</Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <Button 
                        className="bg-gradient-to-r from-purple-500 to-pink-500"
                        onClick={() => setActiveTab('messages')}
                      >
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Message
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className={cardClass}>
                  <CardContent className="p-8 text-center">
                    <User className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                    <h3 className="font-bold text-lg mb-2">No mentor assigned yet</h3>
                    <p className="text-muted-foreground">A mentor will be assigned to you when you enroll in a course</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* AI Tutor CTA */}
          <Card className={`mt-6 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border-purple-500/20 ${cardClass}`}>
            <CardContent className="p-6 flex items-center gap-6">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-xl mb-1">Need Help?</h3>
                <p className="text-muted-foreground">Ask our AI Tutor for instant help with your creative questions</p>
              </div>
              <Button onClick={() => navigate('/dashboard/ai-tutor')} className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                Ask AI Tutor
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>

      {/* Notifications Dialog */}
      <Dialog open={showNotifications} onOpenChange={setShowNotifications}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Notifications
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[400px]">
            <div className="space-y-3">
              {notifications.length > 0 ? (
                notifications.map((notif) => (
                  <div 
                    key={notif.notification_id}
                    className={`p-3 rounded-xl cursor-pointer transition-colors ${
                      notif.read ? 'bg-muted/50' : 'bg-primary/5 border border-primary/20'
                    }`}
                    onClick={() => markNotificationRead(notif.notification_id)}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        notif.type === 'submission_reviewed' ? 'bg-emerald-500/10' : 
                        notif.type === 'new_message' ? 'bg-purple-500/10' : 'bg-blue-500/10'
                      }`}>
                        {notif.type === 'submission_reviewed' ? (
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                        ) : notif.type === 'new_message' ? (
                          <MessageCircle className="w-4 h-4 text-purple-500" />
                        ) : (
                          <Bell className="w-4 h-4 text-blue-500" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">{notif.title}</p>
                        <p className="text-xs text-muted-foreground">{notif.message}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatTimeAgo(notif.created_at)}
                        </p>
                      </div>
                      {!notif.read && (
                        <div className="w-2 h-2 bg-primary rounded-full" />
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Bell className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No notifications yet</p>
                </div>
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Annotated Image Viewer */}
      {showAnnotatedImage && selectedFeedback && (
        <AnnotatedImageViewer 
          feedback={selectedFeedback}
          onClose={() => setShowAnnotatedImage(false)}
        />
      )}

      {/* Student Profile Modal */}
      {showProfileModal && (
        <StudentProfileModal 
          user={user}
          dashboardData={dashboardData}
          onClose={() => setShowProfileModal(false)}
        />
      )}

      {/* Submit Assignment Dialog */}
      <Dialog open={!!submittingAssignment} onOpenChange={() => { setSubmittingAssignment(null); setSubmissionFile(null); setUploadedFileName(''); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Submit Assignment
            </DialogTitle>
            <DialogDescription>
              {submittingAssignment?.title} - {submittingAssignment?.course_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={handleFileUpload}
            />
            
            <div 
              className={`p-6 border-2 border-dashed rounded-xl text-center cursor-pointer transition-colors ${
                isUploading ? 'bg-primary/5 border-primary' : 'hover:border-primary/50 hover:bg-muted/50'
              }`}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              {isUploading ? (
                <>
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-3"></div>
                  <p className="text-sm text-muted-foreground">Uploading...</p>
                </>
              ) : uploadedFileName ? (
                <>
                  <CheckCircle className="w-12 h-12 mx-auto mb-3 text-emerald-500" />
                  <p className="text-sm font-medium text-emerald-600 mb-1">File uploaded!</p>
                  <p className="text-xs text-muted-foreground">{uploadedFileName}</p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-3"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSubmissionFile(null);
                      setUploadedFileName('');
                    }}
                  >
                    Remove & Upload Different File
                  </Button>
                </>
              ) : (
                <>
                  <ImageIcon className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
                  <p className="text-sm font-medium mb-1">
                    Drag and drop your file here
                  </p>
                  <p className="text-sm text-muted-foreground mb-3">
                    or click to browse
                  </p>
                  <Button variant="outline" size="sm">
                    <Upload className="w-4 h-4 mr-2" />
                    Browse Files
                  </Button>
                </>
              )}
            </div>
            
            <p className="text-xs text-muted-foreground text-center">
              Accepted: JPG, PNG, WebP, GIF • Max 5MB
            </p>
            
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">Or paste URL</span>
              </div>
            </div>
            
            <Input
              type="text"
              placeholder="Paste image URL here..."
              value={!uploadedFileName ? (submissionFile || '') : ''}
              onChange={(e) => {
                setSubmissionFile(e.target.value);
                setUploadedFileName('');
              }}
              disabled={!!uploadedFileName}
            />
            
            <div className="text-sm text-muted-foreground border-t pt-4">
              <p><strong>Due:</strong> {submittingAssignment?.due_date ? new Date(submittingAssignment.due_date).toLocaleDateString() : 'N/A'}</p>
              <p><strong>Max Score:</strong> {submittingAssignment?.max_score || 100}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setSubmittingAssignment(null); setSubmissionFile(null); setUploadedFileName(''); }}>Cancel</Button>
            <Button 
              onClick={handleSubmitAssignment} 
              className="bg-gradient-to-r from-emerald-500 to-cyan-500"
              disabled={!submissionFile || isUploading || isSubmitting}
            >
              <Upload className="w-4 h-4 mr-2" />
              {isSubmitting ? 'Submitting...' : isUploading ? 'Uploading...' : 'Submit Assignment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
